import React, { useState, useEffect } from 'react';
import { Grid, IconButton, MenuItem, Popper, Paper, Tooltip, Chip, Stack, Skeleton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RecyclingIcon from '@mui/icons-material/Recycling';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { isDesktop } from '../../utils';
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    AddButton,
    BulkModal,
    DataGrid,
    DropdownButton,
} from '../Common';
import CreateIcon from '@mui/icons-material/Create';
import HistoryIcon from '@mui/icons-material/History';
import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import { TranslateW } from '../../Utils/Languages/Translate';
import {
    dataGridContainerProps,
    text_commonCellProps,
    select_commonCellProps,
    getLocalStorageKey
} from '../Common/DataGrid/utils';
import { StyledCellLink, Percentage, Weight } from '../Common/DataGrid/CellComponents';
import {
    getDateRangeFilterOperator,
    getRangeFilterOperator
} from '../Common/DataGrid/FilterComponents';
import { Grey2, regulatedAssetTagColors } from '../../color_utils';
import {
    useBreadCrumbs,
    useNavigate,
    useBulkUpdates,
} from '../../hooks';
import Enums from '../../enums';

const batchUpdateType = "assets";
const dateRangeFilterOperator = getDateRangeFilterOperator({});
const rangeFilterOperator = getRangeFilterOperator({});
const textStyle = {
    fontFamily: 'Open Sans',
    fontSize: '12px',
    fontWeight: 600,
    color: '#000',
};

const Inventory = ({ history }) => {
    const [filters, setFilters] = useState([]);
    const [menuAnchorElement, setMenuAnchorElement] = useState(null);
    const [batchUpdateModelOpen, setBatchUpdateModelOpen] = useState(false);
    const [allRegulatedAssetTypes, setAllRegulatedAssetTypes] = useState([]);
    const [loading, setLoading] = useState(true);

    const { isLoading, BatchUpdateExport, BatchUpdateImport } = useBulkUpdates();

    const navigate = useNavigate({ pageName: 'Inventory' });

    useBreadCrumbs({
        path: { id: 'Inventory', text: 'Inventory', link: history.location.pathname, reset: true }
    });

    const recycleOptions = [
        { label: 'Move Cylinders', value: 'move_cylinders', onClick: () => navigate.push('/MoveCylinders') },
        {
            label: 'Send Cylinders to Recycling',
            value: 'send_cylinders_recycling',
            onClick: () => navigate.push('/SendCylindersToRecycling')
        },
    ];
    const handleRecycleClick = (e) => {
        if (Boolean(menuAnchorElement)) {
            handlePopoverClose();
        } else {
            handlePopoverOpen(e);
        }
    };

    useEffect(() => {
        fetchData();

        const savedTableState = JSON.parse(localStorage.getItem(getLocalStorageKey(
            Routes.Asset.AssetTable()
        )));
        const assetTableCacheCleared = localStorage.getItem("assetTableCacheCleared");

        if (savedTableState && !assetTableCacheCleared) {
            localStorage.removeItem(getLocalStorageKey(Routes.Asset.AssetTable()));
            localStorage.setItem("assetTableCacheCleared", "true");
        }
    }, []);

    const fetchData = async () => {
        const filterQuery = fetch(Routes.Asset.TableFilters());
        const allRegulatedAssetTypesQuery = fetch(Routes.RegulatedAssetType.GetAll());
        await Promise.all([filterQuery, allRegulatedAssetTypesQuery])
            .then(async ([rawFilters, rawRegulatedAssetTypes]) => {
                const filters = await rawFilters.json();
                const regulatedAssetTypes = await rawRegulatedAssetTypes.json();
                
                const includedIds = [1, 8, 2, 3, 6, 4]
                const filteredRegulatedAssetTypes = regulatedAssetTypes
                    .filter(x => includedIds.includes(x.id))
                    .sort((a, b) => a.name.localeCompare(b.name))
                console.log('what this', filteredRegulatedAssetTypes)

                setFilters(filters);
                setAllRegulatedAssetTypes(filteredRegulatedAssetTypes);
                setLoading(false);
            });
    };

    const handlePopoverOpen = event => setMenuAnchorElement(event.currentTarget);

    const handlePopoverClose = () => setMenuAnchorElement(null);

    const allowExport = () => {
        return UserInfo.isSystemAdministrator() ||
            UserInfo.isTenantAdministrator() ||
            UserInfo.isTechnicianManager() ||
            UserInfo.isTechnician() ||
            UserInfo.isSustainabilityManager() ||
            UserInfo.isReadOnly() ||
            UserInfo.isSuperTenantAdmin() || 
            UserInfo.isRegionalAdministrator();
    };

    const isTechOrTenantContractor = () => (
        UserInfo.isNativeAccountServiceProvider()
        && (
            UserInfo.isTenantAdministrator()
            || UserInfo.isTechnicianManager()
            || UserInfo.isTechnician()
        )
    );

    const isTechSystemOwner = () => (
        UserInfo.isNativeSystemOwner() && (UserInfo.isTechnicianManager() || UserInfo.isTechnician())
    );

    const renderCreateInventoryButton = (
        UserInfo.isSystemAdministrator()
        || isTechOrTenantContractor()
        || isTechSystemOwner()
        || (UserInfo.isNativeUser() && (UserInfo.isAdminRole() || UserInfo.isRegionalAdministrator()))
    );

    const columns = [
        {
            field: 'id',
            headerName: 'Id',
            initiallyVisible: false,
            ...text_commonCellProps,
            renderCell: ({ row: { id } }) => (
                <StyledCellLink
                    path={`/Inventory/${id}`}
                    text={id}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'name',
            headerName: 'Inventory Name',
            ...text_commonCellProps,
            renderCell: ({ row: { name, id } }) => (
                <StyledCellLink
                    primaryLink
                    path={`/Inventory/${id}`}
                    text={name}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'locationName',
            headerName: 'Location',
            ...select_commonCellProps,
            renderCell: ({ row: { locationName, locationId } }) => (
                isDesktop ? (
                    <StyledCellLink
                        path={`/Location/${locationId}`}
                        text={locationName}
                        from={window.location.pathname}
                    />
                ) : (
                    locationName
                )
            )
        },
        {
            field: 'locationState',
            headerName: 'State',
            ...select_commonCellProps
        },
        {
            field: 'regionDivision',
            headerName: 'Region/Division',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'status',
            headerName: 'Status',
            ...select_commonCellProps
        },
        {
            field: 'operationalStatus',
            headerName: 'Operational Status',
            ...select_commonCellProps
        },
        {
            field: 'type',
            headerName: 'Type',
            ...select_commonCellProps
        },
        {
            field: 'materialType',
            headerName: 'Material Type',
            ...select_commonCellProps
        },
        {
            field: 'chargeCapacity',
            headerName: 'Charge Capacity',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
            renderCell: ({ row: { chargeCapacity } }) => <Weight value={chargeCapacity} />
        },
        {
            field: 'applianceCharge',
            headerName: 'Appliance Charge',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
            renderCell: ({ row: { applianceCharge } }) => <Weight value={applianceCharge} />
        },
        {
            field: 'leakRate',
            headerName: 'Epa Leak Rate',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            renderCell: ({ row: { leakRate } }) => <Percentage value={leakRate} />
        },
        {
            field: 'materialClass',
            headerName: 'Material Class',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'systemGWP',
            headerName: 'System GWP',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
        },
        {
            field: 'exceedsEPALeakRate',
            headerName: 'Exceeds EPA Leak Rate',
            initiallyVisible: false,
            sortable: false,
            ...select_commonCellProps,
            renderCell: ({ row: { exceedsEPALeakRate } }) => exceedsEPALeakRate ? "Yes" : ""
        },
        {
            field: 'isChronicallyLeaky',
            headerName: 'Chronically Leaky',
            initiallyVisible: false,
            sortable: false,
            ...select_commonCellProps,
            renderCell: ({ row: { isChronicallyLeaky } }) => isChronicallyLeaky ? "Yes" : ""
        },
        {
            field: 'acquisitionDate',
            headerName: 'Acquisition Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'installDate',
            headerName: 'Install Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'modelNumber',
            headerName: 'Model Number',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'serialNumber',
            headerName: 'Serial Number',
            ...text_commonCellProps
        },
        {
            field: 'regulatedAsset',
            headerName: 'Applicable Regulation',
            valueOptions: allRegulatedAssetTypes.map(f => ({ value: f.id.toString(), label: f.name })),
            sortable: false,
            ...select_commonCellProps,
            renderCell: ({ row: { regulatedAsset } }) => {
                if (!regulatedAsset) return null;
                const allRegulations = regulatedAsset
                    .split(",")
                    .map((regulatedAssetType) => {
                        /* must return an empty object because on first render
                        * allRegulatedAssetTypes has not yet come back from DB
                        */
                        const foundRegulation = allRegulatedAssetTypes.find(r => r.name.toLowerCase() === regulatedAssetType.toLowerCase()) || {};
                        return {
                            ...foundRegulation,
                            color: regulatedAssetTagColors[foundRegulation.id],
                        };
                    }).filter(r => r.isVisibleOnUI);

                return (
                    <Stack direction="row" spacing={1} sx={{ display: 'flex', marginTop: '10px' }}>
                        {(loading && allRegulations.length) ? (
                            [...Array(2)].map(() => (
                                <Skeleton variant="circular" width={55} height={28} />
                            ))
                        ) : (
                            allRegulations.map(({ id, name, color }) => (
                                <Chip
                                    key={id}
                                    label={name}
                                    sx={{
                                        color,
                                        backgroundColor: '#fff',
                                        border: `1px solid ${color}`,
                                        fontSize: '12px',
                                        height: '28px',
                                    }}
                                />
                            ))
                        )}
                    </Stack>
                );
            }
        },
        {
            field: 'safetyClass',
            headerName: 'Safety Class',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'alias',
            headerName: 'Alias',
            initiallyVisible: false,
            filterable: false,
            ...text_commonCellProps
        },
        {
            field: 'createdBy',
            headerName: 'Created By',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'createdByUsername',
            headerName: 'Created By Username',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'createdDate',
            headerName: 'Created Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'roomArea',
            headerName: 'Room/Area',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'locationOnSite',
            headerName: 'Location On Site',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'category',
            headerName: 'Category',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'subType',
            headerName: 'Subtype',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'kind',
            headerName: 'Kind',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'manufacturer',
            headerName: 'Manufacturer',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'modelYear',
            headerName: 'Model Year',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'manufactureDate',
            headerName: 'Manufacture Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'shutdownDate',
            headerName: 'Shutdown Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            type: 'text',
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'systemManager',
            headerName: 'System Manager',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'tagNumber',
            headerName: 'Tag Number',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'cylinderCurrentGasWeightLbs',
            headerName: 'Current Gas Weight',
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
            renderCell: ({ row }) => (
                <Weight
                    value={row.isCylinder ? row.cylinderCurrentGasWeightLbs : row.applianceCharge}
                />
            )

        },
        {
            field: 'cylinderMaxGasCapacityLbs',
            headerName: 'Max Gas Capacity',
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
            renderCell: ({ row }) => {
                const isCylinder = [
                    Enums.AssetTypes.VirginCylinder,
                    Enums.AssetTypes.RecoveryCylinder,
                ].includes(row.typeId);

                let value = row.chargeCapacity;

                if (isCylinder) {
                    value = row.cylinderMaxGasCapacityLbs;
                } else if (row.typeId === Enums.AssetTypes.Switchgear) {
                    value = row.switchGearMaxGasCapacityLbs;
                }

                return <Weight value={value} />;
            }
        },
        {
            type: 'actions',
            field: 'Quick Actions',
            width: 100,
            getActions: ({ row: { id, typeId } }) => {

                const actionItems = [];

                const isVirginOrRecoveryCylinder = [
                    Enums.AssetTypes.VirginCylinder,
                    Enums.AssetTypes.RecoveryCylinder
                ].includes(typeId);

                const isCoolingAppliance = [
                    Enums.AssetTypes.Refrigeration,
                    Enums.AssetTypes.ComfortCooling,
                    Enums.AssetTypes.ProcessCooling,
                    Enums.AssetTypes.ControlsControl
                ].includes(typeId);

                const isEditButtonVisible =
                    (isVirginOrRecoveryCylinder && UserInfo.isNativeSystemOwner() && (UserInfo.isTechnicianManager() || UserInfo.isTenantAdministrator() || UserInfo.isSuperTenantAdmin() || UserInfo.isRegionalAdministrator())) ||
                    (isCoolingAppliance && UserInfo.isNativeSystemOwner() && (UserInfo.isTenantAdministrator() || UserInfo.isSuperTenantAdmin() || UserInfo.isRegionalAdministrator())) ||
                    UserInfo.isSystemAdministrator()

                if (isEditButtonVisible) {
                    actionItems.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip
                                    title="Edit Inventory"
                                >
                                    <CreateIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={() => navigate.push(`/EditInventory/${id}`)}
                        />
                    )
                }
                else {
                    actionItems.push(
                        <IconButton disabled sx={{ width: "30px", height: "30px", padding: "0px", margin: "0px" }} />
                    );
                }

                actionItems.push(
                    <GridActionsCellItem
                        icon={
                            <Tooltip
                                title="View History"
                            >
                                <HistoryIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => navigate.push(`/Inventory/${id}`)}
                    />
                )

                return actionItems;
            }
        },
    ];

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Inventory' }
                ]}
                sx={{ display: { xs: 'none', lg: 'block' } }}
            />
            <Grid
                container
                direction="row"
                alignItems="center"
                sx={{ py: { xs: 2, md: 0, lg: 0 } }}
            >
                <Grid item xs={6} md={6} lg={6} sx={{ px: { xs: 2, md: 0, lg: 0 }}}>
                    <PageTitleWithToolTip
                        pageTitle="Inventory"
                        toolTipText="Inventory hosts the equipments that are tied to a specific location.
                            These can include refrigeration systems, cylinders, and non refrigerant assets like ALDs
                        "
                    />
                </Grid>

                {UserInfo.isSystemAdministrator() && (
                    <>
                        <Grid item sx={{ marginLeft: "auto", display: { xs: 'none', lg: 'block' } }}>
                            <Tooltip title="Export Inventory Template">
                                <IconButton
                                    onClick={(_) => BatchUpdateExport(
                                        batchUpdateType,
                                        { sort: null, filters: [] },
                                    )}
                                >
                                    <img
                                        src="/images/batchUpdateExport.svg"
                                        alt={TranslateW("BatchUpdateExport")}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item sx={{ marginLeft: "1%", display: { xs: 'none', lg: 'block' } }}>
                            <Tooltip title="Bulk Import Inventory">
                                <IconButton
                                    onClick={_ => setBatchUpdateModelOpen(true)}
                                >
                                    <img
                                        src="/images/batchUpdateImport.svg"
                                        alt={TranslateW("BatchUpdateExport")}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </>
                )}

                {/* Desktop version - Recycle IconButton */}
                <Grid
                    item
                    aria-haspopup="true"
                    sx={{
                        marginLeft: UserInfo.isSystemAdministrator() ? "1%" : "auto",
                        ...(UserInfo.isReadOnly() && { display: 'none' }),
                        display: { xs: 'none', md: 'none', lg: 'block' }
                    }}
                    onMouseEnter={handlePopoverOpen}
                >
                    <IconButton sx={{ marginRight: "20px" }}>
                        <img
                            src="/images/ic_recycle.svg"
                            alt={TranslateW("recycle")}
                        />
                    </IconButton>
                </Grid>

                {/* Tablet version */}
                <Grid
                    item
                    xs={12}
                    md="auto"
                    sx={{
                        display: { xs: 'none', md: 'flex', lg: 'none' },
                        marginLeft: 'auto',
                        gap: '12px',
                        alignItems: 'center'
                    }}
                >
                    {!UserInfo.isReadOnly() && (
                        <DropdownButton
                            leftContent={<RecyclingIcon sx={{ fontSize: 24 }} />}
                            rightContent={<KeyboardArrowDownIcon sx={{ fontSize: 20 }} />}
                            onClick={handleRecycleClick}
                            fullWidth={false}
                        />
                    )}
                    {renderCreateInventoryButton && (
                        <AddButton
                            text="Create Inventory"
                            onClick={() => navigate.push('/AddInventory')}
                        />
                    )}
                </Grid>

                {renderCreateInventoryButton && (
                    <Grid item xs={12} lg="auto" sx={{ mt: { xs: 2, lg: 0 }, mb: { xs: 1, lg: 0 }, display: { md: 'none', lg: 'flex' }, px: { xs: 2, md: 0 } }}>
                        <AddButton
                            text="Create Inventory"
                            onClick={() => navigate.push('/AddInventory')}
                            sx={{ width: { xs: '100%', lg: 'auto' }, height: { xs: '42px', lg: 'auto' } }}
                        />
                    </Grid>
                )}

                {!UserInfo.isReadOnly() && (
                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: { xs: 'block', md: 'none', lg: 'none' },
                            px: { xs: 2, md: 0 }
                        }}
                    >
                        <DropdownButton
                            leftContent={<RecyclingIcon sx={{ fontSize: 24 }} />}
                            rightContent={<KeyboardArrowDownIcon sx={{ fontSize: 20 }} />}
                            onClick={handleRecycleClick}
                            fullWidth={true}
                            sx={{ width: { xs: '100%', lg: 'auto' }, height: { xs: '42px', lg: 'auto' } }}
                        />
                    </Grid>
                )}

                <Grid item {...dataGridContainerProps}>
                    <DataGrid
                        enableScan
                        url={Routes.Asset.AssetTable()}
                        exportButtonURL={Routes.Asset.TableExport()}
                        hideExportButton={!allowExport()}
                        exportButtonAlt={TranslateW('ExportInventory')}
                        exportFileName="Inventories"
                        filterOptions={filters}
                        noResultText="Oops...It is empty here..."
                        noResultText2="Create a new Asset or change your search criterias."
                        tableId={"inventory-table"}
                        columns={isDesktop ? columns : columns.filter(col => col.field !== 'Quick Actions')}
                    />
                </Grid>
            </Grid>

            <Popper
                anchorEl={menuAnchorElement}
                open={Boolean(menuAnchorElement)}
                onMouseLeave={handlePopoverClose}
                placement="bottom-start"
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                    },
                ]}
            >
                <Paper sx={{
                    borderRadius: "2px",
                    width: { xs: menuAnchorElement?.offsetWidth || 'auto', md: 'auto', lg: 'auto' },
                    minWidth: { md: '200px' },
                    marginTop: 0
                }} elevation={6}>
                    {recycleOptions.map(o => (
                        <MenuItem onClick={o.onClick} sx={{ ...textStyle,  fontSize: { xs: '16px', lg: '12px' }, }}>
                            {o.label}
                        </MenuItem>
                    ))}
                </Paper>
            </Popper>

            <BulkModal
                open={batchUpdateModelOpen}
                onClose={() => setBatchUpdateModelOpen(false)}
                onClick={async (excelFile) => {
                    await BatchUpdateImport(batchUpdateType, excelFile);
                    setBatchUpdateModelOpen(false);
                }}
                loading={isLoading}
            />
        </>
    );
};

export default Inventory;
